class LazyLoader {
    constructor(elements, options = {}) {
        this.options = Object.assign(
            {
                targetPos: "",
                addpixel: 200,
                maxConcurrentLoads: 3
            },
            options
        );
        this.elements = elements;
        this.loadingCount = 0;
        this.queue = [];

        if (typeof IntersectionObserver != "undefined") {
            this.config = {
                threshold: 0.2,
                rootMargin: "500px",
            };
            this.observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) { 
                        if (this.loadingCount < this.options.maxConcurrentLoads) {
                            this.loadElement(entry.target);
                        } else {
                            this.queue.push(entry.target);
                        }
                        this.observer.unobserve(entry.target);
                    }
                });
            }, this.config);
            this.elements.forEach(element => this.observer.observe(element));
        } else {
            this.elements.forEach(element => this.loadElement(element));
        }
    }

    findVisibleContainer(element) {
        let el = element.parentElement;
        while (el && el !== document.body) {
            const style = window.getComputedStyle(el);
            if (style.display !== "contents" && style.display !== "none") {
                return el;
            }
            el = el.parentElement;
        }
        return null;
    }

    loadElement(element) {
        this.loadingCount++;
        const isImage = element.tagName === 'IMG';
        const isLazySection = element.closest(".c-lazy") != null;

        if (isImage) { 
            requestAnimationFrame(() => {
                const image = element;

                // Forcer un reflow
                element.offsetHeight;

                const container = this.findVisibleContainer(image);
                if (!container) { 
                    this.loadingCount--;
                    this.checkQueue();
                    return;
                }

                const tryGetDimensions = () => {
                    let width = container.offsetWidth || 0;
                    let height = container.offsetHeight || 0;
                    if (!width && !height) {
                        const rect = container.getBoundingClientRect();
                        width = rect.width || 0;
                        height = rect.height || 0;
                    }
                    return { width, height };
                };

                let { width: containerWidth, height: containerHeight } = tryGetDimensions();

                if (!containerWidth && !containerHeight) {
                    image.__lazyRetryCount = (image.__lazyRetryCount || 0) + 1;
                    if (image.__lazyRetryCount > 5) { 
                        this.loadingCount--;
                        this.checkQueue();
                        return;
                    } 
                    this.loadingCount--;
                    requestAnimationFrame(() => this.loadElement(image));
                    return;
                }

                let lcpMob = window.innerWidth < 992 && image.classList.contains("lcp");

                if (lcpMob) {
                    this.loadingCount--;
                    this.checkQueue();
                    return;
                }

                let sizes = [1400, 1200, 1000, 800, 600, 500, 400, 300, 200];
                let closest;
                let urls;

                if (image.classList.contains("ext")) {
                    const url = image.getAttribute("data-src");
                    image.src = url;
                    image.classList.add("loaded");
                    if (isLazySection) {
                        image.closest(".c-lazy").classList.add("c-loaded");
                    }
                    this.loadingCount--;
                    this.checkQueue();
                    return;
                }

                if (containerHeight > containerWidth) {
                    containerHeight += this.options.addpixel;
                    closest = sizes.reduce((prev, curr) =>
                        Math.abs(curr - containerHeight) < Math.abs(prev - containerHeight) ? curr : prev
                    );
                    urls = {
                        1400: image.getAttribute("data-src-1400h"),
                        1200: image.getAttribute("data-src-1200h"),
                        1000: image.getAttribute("data-src-1000h"),
                        800: image.getAttribute("data-src-800h"),
                        600: image.getAttribute("data-src-600h"),
                        500: image.getAttribute("data-src-500h"),
                        400: image.getAttribute("data-src-400h"),
                        300: image.getAttribute("data-src-300h"),
                        200: image.getAttribute("data-src-200h")
                    };
                } else {
                    containerWidth += this.options.addpixel;
                    closest = sizes.reduce((prev, curr) =>
                        Math.abs(curr - containerWidth) < Math.abs(prev - containerWidth) ? curr : prev
                    );
                    urls = {
                        1400: image.getAttribute("data-src-1400w"),
                        1200: image.getAttribute("data-src-1200w"),
                        1000: image.getAttribute("data-src-1000w"),
                        800: image.getAttribute("data-src-800w"),
                        600: image.getAttribute("data-src-600w"),
                        500: image.getAttribute("data-src-500w"),
                        400: image.getAttribute("data-src-400w"),
                        300: image.getAttribute("data-src-300w"),
                        200: image.getAttribute("data-src-200w")
                    };
                }

                const chosenUrl = urls[closest];
                if (!chosenUrl) { 
                    this.loadingCount--;
                    this.checkQueue();
                    return;
                }

                image.src = chosenUrl;
                image.addEventListener('load', () => {
                    image.classList.add('loaded');
                    if (isLazySection) {
                        image.closest(".c-lazy").classList.add("c-loaded");
                    }
                    this.loadingCount--;
                    this.checkQueue();
                }, { once: true });

                image.addEventListener('error', () => { 
                    this.loadingCount--;
                    this.checkQueue();
                }, { once: true });
            });
        } else {
            // Traitement vidéo
            const video = element;
            for (let source of video.children) {
                if (source.tagName === "SOURCE") {
                    source.src = source.dataset.src;
                }
            }

            video.addEventListener('canplaythrough', () => {
                video.classList.add("loaded");
                if (isLazySection) {
                    video.closest(".c-lazy").classList.add("c-loaded");
                }
                this.loadingCount--;
                this.checkQueue();
            }, { once: true });

            video.load();
        }
    }

    checkQueue() {
        if (this.queue.length > 0 && this.loadingCount < this.options.maxConcurrentLoads) {
            const nextElement = this.queue.shift();
            this.loadElement(nextElement);
        }
    }
}

// Initialisation
var lazyImg = new LazyLoader(document.querySelectorAll("img.lazy"));
var lazyVideo = new LazyLoader(document.querySelectorAll("video.lazy"));
